import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useCart } from '../../context/CartContext';
import { useAuth } from '../../context/AuthContext';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { toast } from 'react-hot-toast';
import { CreditCard, CheckCircle, ArrowLeft, ShoppingBag } from 'lucide-react';

const europeanCountries = [
  { code: 'AL', name: 'Albania' },
  { code: 'AD', name: 'Andorra' },
  { code: 'AT', name: 'Austria' },
  { code: 'BY', name: 'Belarus' },
  { code: 'BE', name: 'Belgium' },
  { code: 'BA', name: 'Bosnia and Herzegovina' },
  { code: 'BG', name: 'Bulgaria' },
  { code: 'HR', name: 'Croatia' },
  { code: 'CY', name: 'Cyprus' },
  { code: 'CZ', name: 'Czech Republic' },
  { code: 'DK', name: 'Denmark' },
  { code: 'EE', name: 'Estonia' },
  { code: 'FI', name: 'Finland' },
  { code: 'FR', name: 'France' },
  { code: 'DE', name: 'Germany' },
  { code: 'GR', name: 'Greece' },
  { code: 'HU', name: 'Hungary' },
  { code: 'IS', name: 'Iceland' },
  { code: 'IE', name: 'Ireland' },
  { code: 'IT', name: 'Italy' },
  { code: 'LV', name: 'Latvia' },
  { code: 'LI', name: 'Liechtenstein' },
  { code: 'LT', name: 'Lithuania' },
  { code: 'LU', name: 'Luxembourg' },
  { code: 'MT', name: 'Malta' },
  { code: 'MD', name: 'Moldova' },
  { code: 'MC', name: 'Monaco' },
  { code: 'ME', name: 'Montenegro' },
  { code: 'NL', name: 'Netherlands' },
  { code: 'MK', name: 'North Macedonia' },
  { code: 'NO', name: 'Norway' },
  { code: 'PL', name: 'Poland' },
  { code: 'PT', name: 'Portugal' },
  { code: 'RO', name: 'Romania' },
  { code: 'RU', name: 'Russia' },
  { code: 'SM', name: 'San Marino' },
  { code: 'RS', name: 'Serbia' },
  { code: 'SK', name: 'Slovakia' },
  { code: 'SI', name: 'Slovenia' },
  { code: 'ES', name: 'Spain' },
  { code: 'SE', name: 'Sweden' },
  { code: 'CH', name: 'Switzerland' },
  { code: 'UA', name: 'Ukraine' },
  { code: 'GB', name: 'United Kingdom' },
  { code: 'VA', name: 'Vatican City' }
];

const billingSchema = z.object({
  firstName: z.string().min(2, 'Voornaam moet minimaal 2 tekens bevatten'),
  lastName: z.string().min(2, 'Achternaam moet minimaal 2 tekens bevatten'),
  email: z.string().email('Ongeldig e-mailadres'),
  address: z.string().min(5, 'Adres moet minimaal 5 tekens bevatten'),
  city: z.string().min(2, 'Stad moet minimaal 2 tekens bevatten'),
  postalCode: z.string().min(5, 'Postcode moet minimaal 5 tekens bevatten'),
  country: z.string().min(2, 'Land moet minimaal 2 tekens bevatten'),
});

const paymentSchema = z.object({
  cardNumber: z.string().regex(/^\d{16}$/, 'Kaartnummer moet 16 cijfers bevatten'),
  cardName: z.string().min(2, 'Naam op kaart moet minimaal 2 tekens bevatten'),
  expiryDate: z.string().regex(/^\d{2}\/\d{2}$/, 'Ongeldig formaat (MM/JJ)'),
  cvv: z.string().regex(/^\d{3,4}$/, 'CVV moet 3 of 4 cijfers bevatten'),
});

type BillingFormData = z.infer<typeof billingSchema>;
type PaymentFormData = z.infer<typeof paymentSchema>;

const Checkout: React.FC = () => {
  const { items, totalPrice, clearCart } = useCart();
  const { user } = useAuth();
  const navigate = useNavigate();
  
  const [step, setStep] = useState<'billing' | 'payment' | 'confirmation'>('billing');
  const [billingData, setBillingData] = useState<BillingFormData | null>(null);
  const [orderId, setOrderId] = useState<string>('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  
  const {
    register: registerBilling,
    handleSubmit: handleSubmitBilling,
    formState: { errors: billingErrors, isSubmitting: isBillingSubmitting },
  } = useForm<BillingFormData>({
    resolver: zodResolver(billingSchema),
    defaultValues: {
      firstName: '',
      lastName: '',
      email: user?.email || '',
      address: '',
      city: '',
      postalCode: '',
      country: '',
    },
  });
  
  const {
    register: registerPayment,
    handleSubmit: handleSubmitPayment,
    formState: { errors: paymentErrors, isSubmitting: isPaymentSubmitting },
  } = useForm<PaymentFormData>({
    resolver: zodResolver(paymentSchema),
    defaultValues: {
      cardNumber: '',
      cardName: '',
      expiryDate: '',
      cvv: '',
    },
  });
  
  const onBillingSubmit = (data: BillingFormData) => {
    setBillingData(data);
    setStep('payment');
    window.scrollTo(0, 0);
  };
  
  const onPaymentSubmit = async (data: PaymentFormData) => {
    try {
      if (!user || !billingData) {
        toast.error('Gebruikers- of factuurgegevens ontbreken');
        return;
      }
      
      // Process payment (in a real app, this would call a payment API)
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      // Generate a mock order ID since we're not using Firebase
      const mockOrderId = `ORD-${Date.now().toString().slice(-6)}`;
      setOrderId(mockOrderId);
      
      // Clear the cart
      clearCart();
      
      // Move to confirmation step
      setStep('confirmation');
      window.scrollTo(0, 0);
    } catch (error) {
      console.error('Error processing payment:', error);
      toast.error('Fout bij het verwerken van de betaling. Probeer het opnieuw.');
    }
  };
  
  const handleBackToBilling = () => {
    setStep('billing');
    window.scrollTo(0, 0);
  };
  
  const handleContinueShopping = () => {
    navigate('/contracts');
  };
  
  const handleGoToQuestionnaire = () => {
    navigate(`/questionnaire/${orderId}`);
  };
  
  if (items.length === 0 && step !== 'confirmation') {
    navigate('/cart');
    return null;
  }
  
  const formatCardNumber = (value: string) => {
    return value.replace(/\s/g, '').replace(/(\d{4})/g, '$1 ').trim();
  };
  
  const handleCardNumberChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.replace(/\s/g, '');
    if (/^\d*$/.test(value) && value.length <= 16) {
      e.target.value = formatCardNumber(value);
    }
  };
  
  const handleExpiryDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.replace(/\D/g, '');
    if (value.length <= 4) {
      e.target.value = value.length > 2 ? `${value.slice(0, 2)}/${value.slice(2)}` : value;
    }
  };
  
  return (
    <div className="container mx-auto px-4 py-8">
      <div className="max-w-4xl mx-auto">
        {/* Checkout Steps */}
        <div className="mb-8">
          <div className="flex items-center justify-center">
            <div className={`flex flex-col items-center ${step === 'billing' ? 'text-blue-600' : 'text-gray-500'}`}>
              <div className={`w-10 h-10 flex items-center justify-center rounded-full border-2 ${step === 'billing' ? 'border-blue-600 bg-blue-100' : 'border-gray-300'} mb-2`}>
                1
              </div>
              <span className="text-sm font-medium">Facturering</span>
            </div>
            
            <div className={`w-16 h-0.5 ${step === 'billing' ? 'bg-gray-300' : 'bg-blue-600'} mx-2`}></div>
            
            <div className={`flex flex-col items-center ${step === 'payment' ? 'text-blue-600' : step === 'confirmation' ? 'text-gray-500' : 'text-gray-400'}`}>
              <div className={`w-10 h-10 flex items-center justify-center rounded-full border-2 ${step === 'payment' ? 'border-blue-600 bg-blue-100' : step === 'confirmation' ? 'border-gray-300' : 'border-gray-200'} mb-2`}>
                2
              </div>
              <span className="text-sm font-medium">Betaling</span>
            </div>
            
            <div className={`w-16 h-0.5 ${step === 'confirmation' ? 'bg-blue-600' : 'bg-gray-300'} mx-2`}></div>
            
            <div className={`flex flex-col items-center ${step === 'confirmation' ? 'text-blue-600' : 'text-gray-400'}`}>
              <div className={`w-10 h-10 flex items-center justify-center rounded-full border-2 ${step === 'confirmation' ? 'border-blue-600 bg-blue-100' : 'border-gray-200'} mb-2`}>
                3
              </div>
              <span className="text-sm font-medium">Bevestiging</span>
            </div>
          </div>
        </div>
        
        {step === 'billing' && (
          <div className="bg-white rounded-lg shadow-md overflow-hidden">
            <div className="p-6 border-b border-gray-200">
              <h1 className="text-2xl font-bold text-gray-800">Factuurgegevens</h1>
            </div>
            
            <form onSubmit={handleSubmitBilling(onBillingSubmit)} className="p-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                <div>
                  <label htmlFor="firstName" className="block text-sm font-medium text-gray-700 mb-1">
                    Voornaam
                  </label>
                  <input
                    type="text"
                    id="firstName"
                    className={`block w-full border ${billingErrors.firstName ? 'border-red-300' : 'border-gray-300'} rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm`}
                    {...registerBilling('firstName')}
                  />
                  {billingErrors.firstName && (
                    <p className="mt-1 text-sm text-red-600">{billingErrors.firstName.message}</p>
                  )}
                </div>
                
                <div>
                  <label htmlFor="lastName" className="block text-sm font-medium text-gray-700 mb-1">
                    Achternaam
                  </label>
                  <input
                    type="text"
                    id="lastName"
                    className={`block w-full border ${billingErrors.lastName ? 'border-red-300' : 'border-gray-300'} rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm`}
                    {...registerBilling('lastName')}
                  />
                  {billingErrors.lastName && (
                    <p className="mt-1 text-sm text-red-600">{billingErrors.lastName.message}</p>
                  )}
                </div>
              </div>
              
              <div className="mb-6">
                <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                  E-mail
                </label>
                <input
                  type="email"
                  id="email"
                  className={`block w-full border ${billingErrors.email ? 'border-red-300' : 'border-gray-300'} rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm`}
                  {...registerBilling('email')}
                />
                {billingErrors.email && (
                  <p className="mt-1 text-sm text-red-600">{billingErrors.email.message}</p>
                )}
              </div>
              
              <div className="mb-6">
                <label htmlFor="address" className="block text-sm font-medium text-gray-700 mb-1">
                  Adres
                </label>
                <input
                  type="text"
                  id="address"
                  className={`block w-full border ${billingErrors.address ? 'border-red-300' : 'border-gray-300'} rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm`}
                  {...registerBilling('address')}
                />
                {billingErrors.address && (
                  <p className="mt-1 text-sm text-red-600">{billingErrors.address.message}</p>
                )}
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
                <div>
                  <label htmlFor="city" className="block text-sm font-medium text-gray-700 mb-1">
                    Stad
                  </label>
                  <input
                    type="text"
                    id="city"
                    className={`block w-full border ${billingErrors.city ? 'border-red-300' : 'border-gray-300'} rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm`}
                    {...registerBilling('city')}
                  />
                  {billingErrors.city && (
                    <p className="mt-1 text-sm text-red-600">{billingErrors.city.message}</p>
                  )}
                </div>
                
                <div>
                  <label htmlFor="postalCode" className="block text-sm font-medium text-gray-700 mb-1">
                    Postcode
                  </label>
                  <input
                    type="text"
                    id="postalCode"
                    className={`block w-full border ${billingErrors.postalCode ? 'border-red-300' : 'border-gray-300'} rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm`}
                    {...registerBilling('postalCode')}
                  />
                  {billingErrors.postalCode && (
                    <p className="mt-1 text-sm text-red-600">{billingErrors.postalCode.message}</p>
                  )}
                </div>
                
                <div>
                  <label htmlFor="country" className="block text-sm font-medium text-gray-700 mb-1">
                    Land (optioneel)
                  </label>
                  <select
                    id="country"
                    className="block w-full border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                    {...registerBilling('country')}
                  >
                    <option value="">Selecteer een land</option>
                    {europeanCountries.map((country) => (
                      <option key={country.code} value={country.code}>
                        {country.name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
              
              <div className="border-t border-gray-200 pt-6 flex justify-between">
                <button
                  type="button"
                  onClick={() => navigate('/cart')}
                  className="flex items-center text-blue-600 hover:text-blue-800"
                >
                  <ArrowLeft className="h-4 w-4 mr-2" />
                  Terug naar winkelwagen
                </button>
                
                <button
                  type="submit"
                  disabled={isBillingSubmitting}
                  className="bg-blue-600 hover:bg-blue-700 text-white font-medium py-2 px-6 rounded-md transition duration-300 disabled:opacity-50"
                >
                  Doorgaan naar betaling
                </button>
              </div>
            </form>
          </div>
        )}
        
        {step === 'payment' && (
          <div className="bg-white rounded-lg shadow-md overflow-hidden">
            <div className="p-6 border-b border-gray-200">
              <h1 className="text-2xl font-bold text-gray-800">Betalingsgegevens</h1>
            </div>
            
            <div className="p-6">
              <div className="mb-6">
                <h2 className="text-lg font-semibold mb-4">Besteloverzicht</h2>
                
                <div className="bg-gray-50 rounded-md p-4 mb-4">
                  <div className="flow-root">
                    <ul className="divide-y divide-gray-200">
                      {items.map((item) => (
                        <li key={item.contract.id} className="py-3 flex justify-between">
                          <div>
                            <p className="text-sm font-medium text-gray-900">
                              {item.contract.name} {item.quantity > 1 && `(x${item.quantity})`}
                            </p>
                          </div>
                          <p className="text-sm font-medium text-gray-900">
                            {item.contract.price * item.quantity} €
                          </p>
                        </li>
                      ))}
                    </ul>
                  </div>
                  
                  <div className="border-t border-gray-200 pt-4 flex justify-between">
                    <p className="text-sm font-medium text-gray-700">Subtotaal</p>
                    <p className="text-sm font-medium text-gray-900">{totalPrice} €</p>
                  </div>
                  
                  <div className="flex justify-between pt-2">
                    <p className="text-sm font-medium text-gray-700">BTW (21%)</p>
                    <p className="text-sm font-medium text-gray-900">{(totalPrice * 0.21).toFixed(2)} €</p>
                  </div>
                  
                  <div className="border-t border-gray-200 pt-4 flex justify-between">
                    <p className="text-base font-semibold text-gray-900">Totaal</p>
                    <p className="text-base font-semibold text-gray-900">{(totalPrice * 1.21).toFixed(2)} €</p>
                  </div>
                </div>
              </div>
              
              <form onSubmit={handleSubmitPayment(onPaymentSubmit)}>
                <div className="mb-6">
                  <h2 className="text-lg font-semibold mb-4">Kaartgegevens</h2>
                  
                  <div className="mb-4">
                    <label htmlFor="cardName" className="block text-sm font-medium text-gray-700 mb-1">
                      Naam op kaart
                    </label>
                    <input
                      type="text"
                      id="cardName"
                      className={`block w-full border ${paymentErrors.cardName ? 'border-red-300' : 'border-gray-300'} rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm`}
                      placeholder="Bijv: Jan Jansen"
                      {...registerPayment('cardName')}
                    />
                    {paymentErrors.cardName && (
                      <p className="mt-1 text-sm text-red-600">{paymentErrors.cardName.message}</p>
                    )}
                  </div>
                  
                  <div className="mb-4">
                    <label htmlFor="cardNumber" className="block text-sm font-medium text-gray-700 mb-1">
                      Kaartnummer
                    </label>
                    <div className="relative">
                      <input
                        type="text"
                        id="cardNumber"
                        className={`block w-full border ${paymentErrors.cardNumber ? 'border-red-300' : 'border-gray-300'} rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm pl-10`}
                        placeholder="1234 5678 9012 3456"
                        {...registerPayment('cardNumber')}
                        onChange={handleCardNumberChange}
                      />
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <CreditCard className="h-5 w-5 text-gray-400" />
                      </div>
                    </div>
                    {paymentErrors.cardNumber && (
                      <p className="mt-1 text-sm text-red-600">{paymentErrors.cardNumber.message}</p>
                    )}
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label htmlFor="expiryDate" className="block text-sm font-medium text-gray-700 mb-1">
                        Vervaldatum (MM/JJ)
                      </label>
                      <input
                        type="text"
                        id="expiryDate"
                        className={`block w-full border ${paymentErrors.expiryDate ? 'border-red-300' : 'border-gray-300'} rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm`}
                        placeholder="MM/JJ"
                        {...registerPayment('expiryDate')}
                        onChange={handleExpiryDateChange}
                      />
                      {paymentErrors.expiryDate && (
                        <p className="mt-1 text-sm text-red-600">{paymentErrors.expiryDate.message}</p>
                      )}
                    </div>
                    
                    <div>
                      <label htmlFor="cvv" className="block text-sm font-medium text-gray-700 mb-1">
                        CVV
                      </label>
                      <input
                        type="text"
                        id="cvv"
                        className={`block w-full border ${paymentErrors.cvv ? 'border-red-300' : 'border-gray-300'} rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm`}
                        placeholder="123"
                        maxLength={4}
                        {...registerPayment('cvv')}
                      />
                      {paymentErrors.cvv && (
                        <p className="mt-1 text-sm text-red-600">{paymentErrors.cvv.message}</p>
                      )}
                    </div>
                  </div>
                </div>
                
                <div className="border-t border-gray-200 pt-6 flex justify-between">
                  <button
                    type="button"
                    onClick={handleBackToBilling}
                    className="flex items-center text-blue-600 hover:text-blue-800"
                  >
                    <ArrowLeft className="h-4 w-4 mr-2" />
                    Terug naar factuurgegevens
                  </button>
                  
                  <button
                    type="submit"
                    disabled={isPaymentSubmitting}
                    className="bg-blue-600 hover:bg-blue-700 text-white font-medium py-2 px-6 rounded-md transition duration-300 disabled:opacity-50"
                  >
                    {isPaymentSubmitting ? 'Verwerken...' : 'Betalen'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
        
        {step === 'confirmation' && (
          <div className="bg-white rounded-lg shadow-md overflow-hidden">
            <div className="p-6 text-center">
              <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-green-100 mb-6">
                <CheckCircle className="h-10 w-10 text-green-600" />
              </div>
              
              <h1 className="text-2xl font-bold text-gray-800 mb-4">Bestelling bevestigd</h1>
              
              <p className="text-gray-600 mb-6">
                Bedankt voor uw bestelling! Uw bestelnummer is <span className="font-semibold">{orderId}</span>.
                We hebben een bevestigingsmail gestuurd met de details van uw bestelling.
              </p>
              
              <div className="bg-gray-50 rounded-md p-6 mb-6 text-left">
                <h2 className="text-lg font-semibold mb-4">Volgende stappen</h2>
                
                <ol className="space-y-4">
                  <li className="flex">
                    <span className="bg-blue-100 text-blue-700 rounded-full h-6 w-6 flex items-center justify-center font-semibold mr-3 flex-shrink-0">1</span>
                    <div>
                      <p className="font-medium">Vul de vragenlijst in</p>
                      <p className="text-sm text-gray-600">
                        Om uw contract op maat te maken, vult u de vragenlijst in die specifiek is voor uw bestelling.
                      </p>
                    </div>
                  </li>
                  
                  <li className="flex">
                    <span className="bg-blue-100 text-blue-700 rounded-full h-6 w-6 flex items-center justify-center font-semibold mr-3 flex-shrink-0">2</span>
                    <div>
                      <p className="font-medium">Verwerking door ons team</p>
                      <p className="text-sm text-gray-600">
                        Ons juridische team analyseert uw antwoorden en bereidt uw aangepaste contract voor.
                      </p>
                    </div>
                  </li>
                  
                  <li className="flex">
                    <span className="bg-blue-100 text-blue-700 rounded-full h-6 w-6 flex items-center justify-center font-semibold mr-3 flex-shrink-0">3</span>
                    <div>
                      <p className="font-medium">Ontvangst van uw contract</p>
                      <p className="text-sm text-gray-600">
                        U ontvangt uw afgeronde contract binnen de aangegeven termijn. U kunt het downloaden vanuit uw klantomgeving.
                      </p>
                    </div>
                  </li>
                </ol>
              </div>
              
              <div className="flex flex-col sm:flex-row justify-center space-y-4 sm:space-y-0 sm:space-x-4">
                <button
                  onClick={handleGoToQuestionnaire}
                  className="bg-blue-600 hover:bg-blue-700 text-white font-medium py-3 px-6 rounded-md transition duration-300"
                >
                  Vragenlijst invullen
                </button>
                
                <button
                  onClick={handleContinueShopping}
                  className="flex items-center justify-center border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 font-medium py-3 px-6 rounded-md transition duration-300"
                >
                  <ShoppingBag className="h-5 w-5 mr-2" />
                  Verder winkelen
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Checkout;